import { useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth';
import { useAccounts } from '../hooks/accounts';
import { Badge, cn } from './ui';

const NAV: Array<{ to: string; label: string; end?: boolean }> = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/positions', label: 'Positions' },
  { to: '/strategies', label: 'Strategies' },
  { to: '/signals', label: 'Signals' },
  { to: '/models', label: 'Models' },
  { to: '/datasets', label: 'Datasets' },
  { to: '/backtests', label: 'Backtests' },
  { to: '/events', label: 'Events' },
  { to: '/audit', label: 'Audit' },
  { to: '/accounts', label: 'Accounts' },
  { to: '/operations', label: 'Operations' },
];

function ModeBanner() {
  const { data } = useQuery({
    queryKey: ['system-status'],
    queryFn: api.systemStatus,
    refetchInterval: 30_000,
  });
  if (!data) return null;

  const mode = data.trading_mode;
  const live = data.live_trading_enabled;
  const tone = live || mode === 'live' ? 'bad' : mode === 'demo' ? 'warn' : 'info';
  const bg =
    tone === 'bad'
      ? 'bg-red-950/70 border-red-800 text-red-200'
      : tone === 'warn'
        ? 'bg-amber-950/60 border-amber-800 text-amber-200'
        : 'bg-sky-950/50 border-sky-900 text-sky-200';

  return (
    <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-1 border-b px-4 py-1.5 text-xs', bg)}>
      <span className="font-semibold uppercase tracking-wide">
        {mode} mode{live ? ' · LIVE TRADING ENABLED' : ''}
      </span>
      <span className="opacity-80">env {data.environment}</span>
      <span className="opacity-80">·</span>
      <span className="opacity-80">{data.strategies_enabled} strategies enabled</span>
      <span className="opacity-80">·</span>
      <span className="opacity-80">{data.open_positions} open positions</span>
      {data.accounts_halted > 0 && (
        <span className="font-semibold text-red-300">
          · {data.accounts_halted} account(s) HALTED
        </span>
      )}
      {data.operator_events > 0 && (
        <span className="font-semibold text-amber-300">
          · {data.operator_events} incident(s) need an operator
        </span>
      )}
    </div>
  );
}

function AccountSwitcher() {
  const { accounts, selectedId, select } = useAccounts();
  if (accounts.length === 0) return null;
  return (
    <select
      value={selectedId ?? ''}
      onChange={(e) => select(Number(e.target.value))}
      className="rounded border border-edge bg-surface px-2 py-1 text-xs text-slate-200 outline-none focus:border-slate-500"
    >
      {accounts.map((a) => (
        <option key={a.id} value={a.id}>
          #{a.id} {a.name} ({a.trading_mode})
        </option>
      ))}
    </select>
  );
}

export default function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen">
      <ModeBanner />
      <div className="flex">
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-20 w-52 shrink-0 border-r border-edge bg-panel px-3 py-4 transition-transform md:static md:translate-x-0',
            open ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="mb-4 px-2 text-sm font-semibold tracking-tight text-slate-100">ATLAS</div>
          <nav className="space-y-0.5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'block rounded px-2 py-1.5 text-sm',
                    isActive
                      ? 'bg-slate-700/40 text-slate-100'
                      : 'text-slate-400 hover:bg-slate-800/40 hover:text-slate-200',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {open && (
          <div
            className="fixed inset-0 z-10 bg-black/50 md:hidden"
            onClick={() => setOpen(false)}
            aria-hidden
          />
        )}

        <div className="flex min-w-0 flex-1 flex-col md:ml-0">
          <header className="flex items-center justify-between gap-3 border-b border-edge px-4 py-2">
            <button
              className="text-slate-400 md:hidden"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle navigation"
            >
              ☰
            </button>
            <div className="flex flex-1 items-center gap-3">
              <AccountSwitcher />
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-400">
              <span className="hidden sm:inline">{user?.email}</span>
              {user && <Badge tone="neutral">{user.role}</Badge>}
              <button onClick={logout} className="hover:text-slate-200">
                Sign out
              </button>
            </div>
          </header>

          <main key={location.pathname} className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-lg font-semibold text-slate-100">{title}</h1>
        {description != null && <p className="mt-0.5 text-sm text-slate-400">{description}</p>}
      </div>
      {actions != null && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
